import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Pencil, Images, Lock, Mail, MessageSquare, Share2, ScanEye, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from '@/components/ui/sonner';
import { adminListAllGalleries, adminDeleteGallery, adminNotifyGalleryReady, apiErrorMessage } from '@/lib/api';
import { ADMIN_DASHBOARD, GALLERY_UPLOAD, SHARE_LINK } from '@/constants/testIds';
import { GalleryFormDialog } from './galleries/GalleryFormDialog';
import { GalleryImagesDialog } from './galleries/GalleryImagesDialog';
import { ProofingDialog } from './galleries/ProofingDialog';
import { ShareLinkDialog } from './galleries/ShareLinkDialog';
import { goldButtonClass, iconButtonClass } from './galleries/styles';

const GalleryRow = ({ gallery: g, onImages, onProofing, onShare, onNotify, onEdit, onDelete }) => (
  <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-[#141416]" data-testid={`admin-gallery-row-${g.slug}`}>
    <TableCell className="text-[hsl(var(--lxra-fg))]">{g.name}</TableCell>
    <TableCell className="text-[hsl(var(--lxra-muted))]">{g.slug}</TableCell>
    <TableCell>
      {g.is_private ? (
        <Badge variant="outline" className="border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] gap-1">
          <Lock className="h-3 w-3" /> Private
        </Badge>
      ) : (
        <Badge variant="outline" className="border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))]">Public</Badge>
      )}
    </TableCell>
    <TableCell className="text-right space-x-1">
      <Button size="icon" variant="ghost" onClick={onImages} data-testid={`admin-gallery-images-${g.slug}`} className={iconButtonClass} title="Manage photos">
        <Images className="h-4 w-4" />
      </Button>
      <Button size="icon" variant="ghost" asChild data-testid={`admin-gallery-cull-${g.slug}`} className={iconButtonClass} title="Culling Assist — flag blurry, blinks & duplicates">
        <Link to={`/admin/galleries/${g.slug}/cull`}><ScanEye className="h-4 w-4" /></Link>
      </Button>
      <Button size="icon" variant="ghost" asChild data-testid={`admin-gallery-color-${g.slug}`} className={iconButtonClass} title="Color Studio — colour analysis & grading recommendations">
        <Link to={`/admin/galleries/${g.slug}/color`}><Palette className="h-4 w-4" /></Link>
      </Button>
      {g.is_private && (
        <>
          <Button size="icon" variant="ghost" onClick={onProofing} data-testid={GALLERY_UPLOAD.proofingButton(g.slug)} className={`relative ${iconButtonClass}`} title="View selections & comments">
            <MessageSquare className="h-4 w-4" />
            {g.open_revision_requests > 0 && (
              <span
                data-testid={GALLERY_UPLOAD.revisionBadge(g.slug)}
                className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[9px] font-bold text-[#0A0A0A]"
              >
                {g.open_revision_requests}
              </span>
            )}
          </Button>
          <Button size="icon" variant="ghost" onClick={onShare} data-testid={SHARE_LINK.adminButton(g.slug)} className={iconButtonClass} title="Share preview link">
            <Share2 className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={onNotify} data-testid={GALLERY_UPLOAD.notifyButton(g.slug)} className={iconButtonClass} title="Notify client gallery is ready">
            <Mail className="h-4 w-4" />
          </Button>
        </>
      )}
      <Button size="icon" variant="ghost" onClick={onEdit} data-testid={`admin-gallery-edit-${g.slug}`} className={iconButtonClass} title="Edit gallery">
        <Pencil className="h-4 w-4" />
      </Button>
      <Button size="icon" variant="ghost" onClick={onDelete} data-testid={`admin-gallery-delete-${g.slug}`} className="text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10" title="Delete gallery">
        <Trash2 className="h-4 w-4" />
      </Button>
    </TableCell>
  </TableRow>
);

/**
 * Admin → Galleries: list of gallery categories with per-row actions. Each
 * action opens a focused dialog component that owns its own state/loading.
 */
export const AdminGalleriesTab = () => {
  const [galleries, setGalleries] = useState(null);
  // Which dialog is open and for which gallery: { kind: 'form'|'images'|'proofing'|'share', gallery }
  const [dialog, setDialog] = useState(null);

  const refresh = useCallback(() => adminListAllGalleries().then(setGalleries).catch(() => setGalleries([])), []);
  useEffect(() => { refresh(); }, [refresh]);

  const open = (kind, gallery = null) => setDialog({ kind, gallery });
  const closeDialog = (isOpen) => { if (!isOpen) setDialog(null); };
  const isOpen = (kind) => dialog?.kind === kind;
  const activeSlug = dialog?.gallery?.slug || null;

  const handleDelete = async (g) => {
    if (!window.confirm(`Delete gallery "${g.name}" and all its images? This cannot be undone.`)) return;
    try {
      await adminDeleteGallery(g.slug);
      toast.success('Gallery deleted.');
      refresh();
    } catch (err) {
      toast.error('Failed to delete gallery.');
    }
  };

  const handleNotify = async (g) => {
    try {
      const res = await adminNotifyGalleryReady(g.slug);
      const anySent = res.notified?.some((n) => n.status === 'sent');
      const anySkipped = res.notified?.some((n) => n.status === 'skipped');
      if (anySent) toast.success(`Notified ${res.notified.length} client(s) that "${g.name}" is ready.`);
      else if (anySkipped) toast.success('Queued — email will send once SendGrid is configured.');
      else toast.error('Could not send the notification email.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No client account is linked to this gallery yet.'));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[hsl(var(--lxra-muted))]">Manage gallery categories and their photos. Upload photos with the <Images className="inline h-3.5 w-3.5 align-text-bottom" /> button on a gallery — clients see them at once.</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild data-testid="admin-style-library-link" className="h-10 px-3 rounded-lg border-[hsl(var(--lxra-border))] bg-transparent text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] text-xs uppercase tracking-[0.1em]">
            <Link to="/admin/color/styles"><Palette className="h-4 w-4 mr-1.5" /> Style Library</Link>
          </Button>
          <Button onClick={() => open('form')} data-testid={ADMIN_DASHBOARD.createGalleryButton} className={`h-10 px-4 ${goldButtonClass}`}>
            <Plus className="h-4 w-4 mr-1.5" />
            New Gallery
          </Button>
        </div>
      </div>

      {!galleries && <Skeleton className="h-64 rounded-2xl bg-[#141416]" />}

      {galleries && (
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden" data-testid="admin-galleries-table">
          <Table>
            <TableHeader>
              <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-transparent">
                <TableHead className="text-[hsl(var(--lxra-muted))]">Name</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Slug</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Visibility</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {galleries.map((g) => (
                <GalleryRow
                  key={g.slug}
                  gallery={g}
                  onImages={() => open('images', g)}
                  onProofing={() => open('proofing', g)}
                  onShare={() => open('share', g)}
                  onNotify={() => handleNotify(g)}
                  onEdit={() => open('form', g)}
                  onDelete={() => handleDelete(g)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <GalleryFormDialog open={isOpen('form')} onOpenChange={closeDialog} gallery={isOpen('form') ? dialog.gallery : null} onSaved={refresh} />
      <GalleryImagesDialog open={isOpen('images')} onOpenChange={closeDialog} slug={activeSlug} />
      <ProofingDialog open={isOpen('proofing')} onOpenChange={closeDialog} slug={activeSlug} />
      <ShareLinkDialog open={isOpen('share')} onOpenChange={closeDialog} slug={activeSlug} />
    </div>
  );
};
